(() => {
  const releaseAliaScrollLock = () => {
    if (!document.querySelector('[data-pa-builder]')) return;
    [document.documentElement, document.body].forEach((element) => {
      if (!element) return;
      ['overflow', 'overflow-y', 'position', 'inset', 'top', 'right', 'bottom', 'left', 'height', 'width']
        .forEach((property) => element.style.removeProperty(property));
    });
    document.querySelectorAll('[id^="alia-root"], [id^="alia-popup-root"]').forEach((element) => {
      element.inert = true;
      element.setAttribute('inert', '');
      element.hidden = true;
      element.setAttribute('aria-hidden', 'true');
      element.style.setProperty('display', 'none', 'important');
      element.style.setProperty('pointer-events', 'none', 'important');
    });
  };

  releaseAliaScrollLock();
  const aliaLockObserver = new MutationObserver(releaseAliaScrollLock);
  aliaLockObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
  if (document.body) {
    aliaLockObserver.observe(document.body, { attributes: true, attributeFilter: ['style'], childList: true });
  }
  window.addEventListener('pageshow', releaseAliaScrollLock);

  document.querySelectorAll('[data-pa-builder]').forEach((root) => {
    const cards = [...root.querySelectorAll('[data-pa-product]')];
    const products = new Map();
    const basket = new Map();
    const selectionLedger = window.PaBuilderSelectionLedger;
    let selections = [];
    let selectionSequence = 0;
    const threshold = Number(root.dataset.threshold) || 10000;
    // Free-ammo options from the first screen, in section order. The shopper's pick sets the gift
    // variant, the loadout contents and every "FREE 9MM · 50 rounds" line on the page.
    let rewards = [];
    try { rewards = JSON.parse(root.dataset.rewards || '[]'); } catch (_) {}
    rewards = rewards.filter((option) => option && Number(option.variant) > 0 && Number(option.rounds) > 0);
    let reward = rewards[0] || { key: 'primary', label: '9mm', rounds: 50, value: 0, variant: 0, loadout: 'pistol' };
    const findReward = (value) => rewards.find((option) => option.key === value || option.label.toLowerCase() === String(value || '').toLowerCase());
    // Only a choice the shopper actually made is shown as selected on the first screen.
    let rewardChosen = false;
    try {
      const savedReward = findReward(sessionStorage.getItem('pa-builder-reward-v1'));
      if (savedReward) { reward = savedReward; rewardChosen = true; }
    } catch (_) {}
    // Retail price of one free box (cents). Zero hides every "$X value" line.
    let rewardValue = Number(reward.value) || 0;
    const rewardName = () => reward.label.toUpperCase();
    // Within this many cents of the next box, the meter switches to its "almost there" state.
    const nearThreshold = Number(root.dataset.nearThreshold) || 2500;
    // One-time bonus boxes from the section setting, "dollars:boxes" pairs such as "500:1,1000:2":
    // the $100 step that crosses $500 pays 1 extra box, the step that crosses $1,000 pays 2 extra.
    const bonusTiers = String(root.dataset.bonusTiers || '')
      .split(',')
      .map((entry) => entry.split(':').map((part) => Number(part.trim())))
      .filter(([dollars, bonus]) => dollars > 0 && Number.isInteger(bonus) && bonus > 0)
      .map(([dollars, bonus]) => ({ at: Math.round(dollars * 100), bonus }))
      .sort((a, b) => a.at - b.at);
    const compactCrate = window.matchMedia('(max-width: 899px)');
    let crateDrop = 0;
    const stages = [
      {
        key: 'popular',
        eyebrow: 'AISLE 1 OF 4 · POPULAR AMMO',
        title: 'Load up on your go-to calibers.',
        description: 'Start with what you shoot most. Everything you add moves your meter toward more FREE {reward}.',
        nextLabel: 'NEXT: SPECIALTY AMMO',
        nextKicker: 'NEXT UP',
        nextDestination: 'SPECIALTY AMMO',
        nextShort: 'SPECIALTY'
      },
      {
        key: 'specialty',
        eyebrow: 'AISLE 2 OF 4 · SPECIALTY AMMO',
        title: 'Add the calibers you came for.',
        description: 'Harder-to-find rounds. Skip this aisle if you don’t need them—everything still counts toward FREE {reward}.',
        nextLabel: 'NEXT: RANGE TARGETS',
        nextKicker: 'NEXT UP',
        nextDestination: 'RANGE TARGETS',
        nextShort: 'TARGETS',
        backLabel: 'BACK: POPULAR AMMO'
      },
      {
        key: 'targets',
        eyebrow: 'AISLE 3 OF 4 · RANGE TARGETS',
        title: 'Put those rounds on target.',
        description: 'Choose your target packs. Every pack moves the meter toward more FREE {reward}.',
        nextLabel: 'NEXT: ENGRAVED CANS',
        nextKicker: 'NEXT UP',
        nextDestination: 'ENGRAVED CANS',
        nextShort: 'CANS',
        backLabel: 'BACK: SPECIALTY AMMO'
      },
      {
        key: 'cans',
        eyebrow: 'AISLE 4 OF 4 · ENGRAVED CANS',
        title: 'Choose a can worth keeping.',
        description: 'Pick the design that fits your range setup. Every can counts toward more FREE {reward}.',
        nextLabel: 'REVIEW & CHECKOUT',
        nextKicker: 'REVIEW',
        nextDestination: 'REVIEW & CHECKOUT',
        nextShort: 'CHECKOUT',
        backLabel: 'BACK: RANGE TARGETS'
      },
      {
        key: 'cart',
        eyebrow: 'REVIEW & CHECKOUT',
        title: 'Your loadout. Your FREE {reward}.',
        description: 'Review your ammo, targets and cans, plus every FREE {reward} box this loadout has unlocked.',
        backLabel: 'BACK: ENGRAVED CANS'
      }
    ];
    let stageIndex = 0;
    let lastShopStage = 0;
    let selectionVersion = 0;
    let syncingCheckout = false;
    let lastMutation = null;
    let lastReward = null;
    let rewardPulseTimer;
    let rewardSettleTimer;
    let celebrationTimer;
    let statusAnnouncementFrame;
    let pendingAnnouncement = '';
    const checkoutButton = root.querySelector('[data-pa-checkout]');
    const checkoutStatus = root.querySelector('[data-pa-checkout-status]');
    const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
    const compactFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
    const money = (cents) => formatter.format(cents / 100);
    const compactMoney = (cents) => cents % 100 === 0
      ? compactFormatter.format(cents / 100)
      : money(cents);
    const productUnitCount = (product, quantity) => product.rounds
      ? quantity * product.rounds
      : product.targets ? quantity * product.targets : quantity;
    const productAmount = (product, quantity) => {
      const count = productUnitCount(product, quantity);
      const label = product.rounds
        ? count === 1 ? 'round' : 'rounds'
        : product.targets ? count === 1 ? 'target' : 'targets'
          : count === 1 ? 'can' : 'cans';
      return `${count.toLocaleString('en-US')} ${label}`;
    };
    const repeatPresetCopy = (product, quantity, count) => {
      const unitCount = productUnitCount(product, quantity);
      let label;
      let singular;
      let plural;
      if (product.rounds) {
        label = `${unitCount.toLocaleString('en-US')}-ROUND BUNDLE`;
        singular = `${unitCount.toLocaleString('en-US')}-round bundle`;
        plural = `${unitCount.toLocaleString('en-US')}-round bundles`;
      } else if (product.targets) {
        label = `${unitCount.toLocaleString('en-US')}-TARGET PACK`;
        singular = `${unitCount.toLocaleString('en-US')}-target pack`;
        plural = `${unitCount.toLocaleString('en-US')}-target packs`;
      } else if (unitCount === 1) {
        label = 'AMMO CAN';
        singular = 'ammo can';
        plural = 'ammo cans';
      } else {
        label = `${unitCount.toLocaleString('en-US')}-CAN SET`;
        singular = `${unitCount.toLocaleString('en-US')}-can set`;
        plural = `${unitCount.toLocaleString('en-US')}-can sets`;
      }
      return {
        label,
        singular,
        plural,
        countLabel: count === 1 ? singular : plural,
        total: productAmount(product, quantity * count)
      };
    };
    cards.forEach((card, order) => {
      const key = card.dataset.key;
      if (!key || !card.dataset.variant || products.has(key)) return;
      products.set(key, {
        key,
        title: card.dataset.title,
        category: card.dataset.category,
        order,
        variant: card.dataset.variant,
        price: Number(card.dataset.price),
        rounds: Number(card.dataset.rounds),
        targets: Number(card.dataset.targets),
        unit: card.dataset.unit,
        max: Number(card.dataset.max),
        image: card.querySelector('img')?.getAttribute('src') || '',
        imageAlt: card.dataset.title,
        caliber: card.dataset.caliber || ''
      });
    });

    const limits = Object.fromEntries([...products].map(([key, product]) => [key, product.max]));
    const rebuildBasket = () => {
      basket.clear();
      const aggregate = selectionLedger ? selectionLedger.aggregate(selections) : {};
      Object.entries(aggregate).forEach(([key, quantity]) => basket.set(key, quantity));
    };
    const persistSelections = () => {
      try {
        sessionStorage.setItem('pa-builder-selections-v1', JSON.stringify(selections));
        sessionStorage.setItem('pa-builder-basket-v1', JSON.stringify(Object.fromEntries(basket)));
      } catch (_) {}
    };
    const nextSelectionId = () => {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') return `pick-${window.crypto.randomUUID()}`;
      return `pick-${Date.now().toString(36)}-${(++selectionSequence).toString(36)}`;
    };

    try {
      const savedSelections = sessionStorage.getItem('pa-builder-selections-v1');
      if (savedSelections !== null) {
        selections = selectionLedger ? selectionLedger.normalize(JSON.parse(savedSelections), limits) : [];
      } else {
        const savedBasket = JSON.parse(sessionStorage.getItem('pa-builder-basket-v1') || '{}');
        const migrated = Object.entries(savedBasket).map(([key, value], index) => ({
          id: `restored-${index + 1}-${key}`,
          key,
          quantity: Math.floor(Number(value))
        }));
        selections = selectionLedger ? selectionLedger.normalize(migrated, limits) : [];
      }
    } catch (_) {
      selections = [];
    }
    rebuildBasket();
    persistSelections();

    try {
      const savedFeedback = JSON.parse(sessionStorage.getItem('pa-builder-feedback-v2') || '{}');
      if (savedFeedback.lastMutation && products.has(savedFeedback.lastMutation.key)) lastMutation = savedFeedback.lastMutation;
      if (savedFeedback.lastReward && products.has(savedFeedback.lastReward.key)) lastReward = savedFeedback.lastReward;
    } catch (_) {
      // Feedback persistence is optional.
    }

    const lines = root.querySelector('[data-pa-lines]');
    const suggestion = root.querySelector('[data-pa-suggestion]');
    const cartStatus = root.querySelector('[data-pa-cart-status]');
    const celebrationLayer = root.querySelector('[data-pa-celebration-layer]');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const el = (tag, className, value) => {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (value != null) node.textContent = value;
      return node;
    };
    const setRewardCopy = (node, value) => {
      const label = reward.label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const parts = String(value).replaceAll('{reward}', reward.label).split(new RegExp(`(FREE(?: ${label})?)`, 'g'));
      node.replaceChildren(...parts.filter(Boolean).map((part) => part.startsWith('FREE')
        ? el('strong', 'pa-builder__reward-inline', part)
        : document.createTextNode(part)));
    };
    const boxWord = (count) => count === 1 ? 'BOX' : 'BOXES';
    const rewardWorth = (gifts) => rewardValue ? compactMoney(gifts * rewardValue) : '';
    const bonusBoxesAt = (subtotal) => bonusTiers.reduce((sum, tier) => sum + (subtotal >= tier.at ? tier.bonus : 0), 0);
    // Single source for the reward rule: 1 box per full $100, plus any bonus tiers reached.
    const giftsAt = (subtotal) => Math.floor(subtotal / threshold) + bonusBoxesAt(subtotal);
    const tierCrossed = (previousSubtotal, nextSubtotal) => [...bonusTiers].reverse()
      .find((tier) => previousSubtotal < tier.at && nextSubtotal >= tier.at) || null;
    // Box numbers that arrive as bonus boxes (e.g. #6 at $500, #12–#13 at $1,000); drawn brass in the crate.
    const bonusSlots = new Set();
    bonusTiers.reduce((earlierBonus, tier) => {
      const base = Math.floor(tier.at / threshold) + earlierBonus;
      for (let extra = 1; extra <= tier.bonus; extra += 1) bonusSlots.add(base + extra);
      return earlierBonus + tier.bonus;
    }, 0);
    // Box-count milestones for very large loadouts; the bonus tiers carry the big moments below them.
    // After the last listed milestone they continue every 50 boxes.
    const milestones = [25, 50];
    const milestoneStep = 50;
    const isMilestoneBox = (slot) => milestones.includes(slot) || (slot > milestones[milestones.length - 1] && slot % milestoneStep === 0);
    const nextMilestoneAfter = (box) => milestones.find((milestone) => milestone > box) || (Math.floor(box / milestoneStep) + 1) * milestoneStep;
    // Returns the highest milestone box crossed by this change, or 0 when none was crossed.
    const milestoneCrossed = (previousGifts, nextGifts) => {
      for (let box = nextGifts; box > previousGifts; box -= 1) {
        if (isMilestoneBox(box)) return box;
      }
      return 0;
    };
    // Android only: iOS Safari has no Vibration API, so this is a silent no-op there.
    const buzz = (pattern) => {
      try { if (typeof navigator.vibrate === 'function') navigator.vibrate(pattern); } catch (_) {}
    };
    const renderCrate = (container, gifts, maxVisible, dropCount = 0, nextGain = 1) => {
      if (!container) return;
      const visible = Math.min(gifts, maxVisible);
      const boxes = [];
      for (let slot = gifts - visible + 1; slot <= gifts; slot += 1) {
        const box = el('i', 'pa-builder__crate-box');
        if (isMilestoneBox(slot) || bonusSlots.has(slot)) box.classList.add('is-milestone');
        if (slot > gifts - dropCount) box.classList.add('is-new');
        boxes.push(box);
      }
      // Overflow count follows the drawn boxes so it reads as "these plus N more".
      if (gifts > visible) boxes.push(el('b', 'pa-builder__crate-more', `+${gifts - visible}${maxVisible >= 20 ? ' MORE' : ''}`));
      const slot = el('i', 'pa-builder__crate-slot', nextGain > 1 ? `×${nextGain}` : null);
      slot.classList.toggle('is-bonus', nextGain > 1);
      boxes.push(slot);
      container.replaceChildren(...boxes);
    };
    // Cheapest preset add that reaches the next free box, preferring items already in the loadout.
    const matchesReward = (product) => (product.caliber || product.title).toLowerCase().includes(reward.label.toLowerCase());
    // How a pick reads in a sentence: "200 rounds of Winchester 9mm", "10 Birchwood Casey Splatter
    // Targets", "1 “We The People” can".
    const pickPhrase = (product, quantity) => {
      const count = productUnitCount(product, quantity);
      if (product.rounds) return `${count.toLocaleString('en-US')} rounds of ${product.title}`;
      if (product.targets) return `${count.toLocaleString('en-US')} ${product.title}`;
      return `${count} “${product.title}” ${count === 1 ? 'can' : 'cans'}`;
    };
    const findGapCloser = (remaining, accept = () => true) => {
      const choices = cards.flatMap((card) => {
        const product = products.get(card.dataset.key);
        if (!product || !accept(product)) return [];
        const currentQuantity = basket.get(product.key) || 0;
        return [...card.querySelectorAll('[data-pa-add]')].map((button) => {
          const quantity = Number(button.dataset.paAdd);
          const targetQuantity = currentQuantity + quantity;
          return { product, quantity, targetQuantity, cost: product.price * quantity };
        }).filter((choice) => choice.quantity > 0 && choice.cost >= remaining && choice.targetQuantity <= choice.product.max);
      });
      choices.sort((a, b) => (a.cost - remaining) - (b.cost - remaining)
        || Number(basket.has(b.product.key)) - Number(basket.has(a.product.key))
        || a.product.order - b.product.order);
      return choices[0] || null;
    };
    const deriveBasketTotals = () => {
      const subtotal = [...basket].reduce((sum, [key, quantity]) => sum + products.get(key).price * quantity, 0);
      const progress = subtotal % threshold;
      const remaining = threshold - progress;
      const gifts = giftsAt(subtotal);
      // Boxes the next $100 crossing pays: 1 normally, more when it lands on a bonus tier.
      const nextStepGain = giftsAt(subtotal + remaining) - gifts;
      return {
        subtotal,
        gifts,
        progress,
        remaining,
        percent: progress / threshold * 100,
        productCount: basket.size,
        bonusGifts: bonusBoxesAt(subtotal),
        nextStepGain,
        bonusTier: nextStepGain > 1 ? bonusTiers.find((tier) => tier.at === subtotal + remaining) || null : null,
        nextTier: bonusTiers.find((tier) => tier.at > subtotal) || null
      };
    };
    const announceStatus = (announcement) => {
      if (!cartStatus || !announcement) return;
      pendingAnnouncement = pendingAnnouncement.startsWith('Reward unlocked:') && !announcement.startsWith('Reward unlocked:')
        ? `${pendingAnnouncement} ${announcement}`
        : announcement;
      if (statusAnnouncementFrame) return;
      cartStatus.textContent = '';
      statusAnnouncementFrame = requestAnimationFrame(() => {
        cartStatus.textContent = pendingAnnouncement;
        pendingAnnouncement = '';
        statusAnnouncementFrame = null;
      });
    };
    const clearRewardPulse = () => {
      clearTimeout(rewardPulseTimer);
      clearTimeout(rewardSettleTimer);
      rewardPulseTimer = 0;
      rewardSettleTimer = 0;
      root.classList.remove('pa-builder--reward-hit');
    };
    const clearRewardCelebration = () => {
      clearTimeout(celebrationTimer);
      celebrationTimer = 0;
      root.classList.remove('pa-builder--reward-celebrating', 'pa-builder--milestone-celebrating');
      celebrationLayer?.replaceChildren();
    };
    const appendConfettiBurst = (x, y, count, spread, name, giftDelta, milestone = false) => {
      if (!celebrationLayer || reducedMotion.matches || !Number.isFinite(x) || !Number.isFinite(y)) return;
      const burst = el('div', 'pa-builder__confetti-burst');
      burst.dataset.paCelebrationBurst = name;
      burst.dataset.giftDelta = String(giftDelta);
      burst.style.left = `${Math.round(x)}px`;
      burst.style.top = `${Math.round(y)}px`;
      const colors = milestone
        ? ['#ffc857', '#ffe29a', '#ffffff', '#00e38f', '#e0a63a']
        : ['#00e38f', '#9affc9', '#ffffff', '#d7394d', '#ffc857'];
      for (let index = 0; index < count; index += 1) {
        const piece = el('i', 'pa-builder__confetti-piece');
        if (index % 5 === 0) piece.classList.add('is-dot');
        if (index % 7 === 0) piece.classList.add('is-large');
        const direction = count === 1 ? 0 : (index + .5) / count * 2 - 1;
        const reach = .76 + (index * 3 % 7) * .055;
        const xDistance = Math.round(direction * spread * reach);
        const midX = Math.round(xDistance * (.46 + index % 3 * .04));
        const midY = -Math.round(spread * (.44 + (index * 7 % 8) * .06));
        const drop = Math.round(spread * (.32 + (index * 5 % 9) * .045));
        const turn = (index % 2 ? -1 : 1) * (210 + index * 43);
        piece.style.setProperty('--pa-piece-color', colors[index % colors.length]);
        piece.style.setProperty('--pa-mid-x', `${midX}px`);
        piece.style.setProperty('--pa-mid-y', `${midY}px`);
        piece.style.setProperty('--pa-x', `${xDistance}px`);
        piece.style.setProperty('--pa-drop', `${drop}px`);
        piece.style.setProperty('--pa-mid-turn', `${Math.round(turn * .52)}deg`);
        piece.style.setProperty('--pa-turn', `${turn}deg`);
        piece.style.setProperty('--pa-delay', `${index % 9 * 18}ms`);
        piece.style.setProperty('--pa-duration', `${1220 + index % 7 * 70}ms`);
        burst.append(piece);
      }
      celebrationLayer.append(burst);
    };
    const celebrationCenter = () => {
      const viewport = window.visualViewport;
      const viewportLeft = viewport?.offsetLeft || 0;
      const viewportTop = viewport?.offsetTop || 0;
      const viewportWidth = viewport?.width || window.innerWidth;
      const viewportHeight = viewport?.height || window.innerHeight;
      const viewportBottom = viewportTop + viewportHeight;
      const stickyShell = root.querySelector('.pa-builder__sticky-shell');
      const stickyRect = stickyShell?.getBoundingClientRect();
      const stickyVisible = Boolean(stickyRect && stickyRect.height && getComputedStyle(stickyShell).position === 'sticky');
      const mobileBar = root.querySelector('.pa-builder__mobile-bar');
      const mobileRect = mobileBar?.getBoundingClientRect();
      const mobileVisible = Boolean(mobileRect && mobileRect.height && getComputedStyle(mobileBar).display !== 'none');
      const safeTop = stickyVisible && stickyRect.top <= viewportTop + 8
        ? Math.max(viewportTop + 12, stickyRect.bottom + 12)
        : viewportTop + 12;
      const safeBottom = mobileVisible && mobileRect.bottom >= viewportBottom - 8
        ? Math.min(viewportBottom - 12, mobileRect.top - 12)
        : viewportBottom - 12;
      return {
        x: viewportLeft + viewportWidth / 2,
        y: safeTop + Math.max(0, safeBottom - safeTop) * .48,
        width: viewportWidth
      };
    };
    const appendUnlockTicket = (giftDelta, next, center, milestone, tier) => {
      if (!celebrationLayer) return;
      const anchor = el('div', 'pa-builder__celebration-anchor is-centered');
      anchor.dataset.paCelebrationTicket = '';
      anchor.dataset.giftDelta = String(giftDelta);
      anchor.style.left = `${Math.round(center.x)}px`;
      anchor.style.top = `${Math.round(center.y)}px`;
      const ticket = el('div', `pa-builder__unlock-ticket${milestone || tier ? ' is-milestone' : ''}`);
      const totalWorth = rewardWorth(next.gifts);
      if (tier) {
        const addedWorth = rewardWorth(giftDelta);
        const upcoming = next.nextTier;
        ticket.append(
          el('span', 'pa-builder__unlock-mark', `${tier.bonus + 1}×`),
          el('span', 'pa-builder__unlock-kicker', `${compactMoney(tier.at)} BONUS UNLOCKED`),
          el('strong', '', `+${giftDelta} FREE ${rewardName()} ${boxWord(giftDelta)}`),
          el('b', 'pa-builder__unlock-rounds', `INCLUDES ${tier.bonus} BONUS ${boxWord(tier.bonus)}${addedWorth ? ` · ${addedWorth} VALUE` : ''}`),
          el('small', '', upcoming
            ? `NEXT BONUS AT ${compactMoney(upcoming.at)} · +${upcoming.bonus} BONUS ${boxWord(upcoming.bonus)}`
            : `${next.gifts} FREE ${boxWord(next.gifts)} · ${(next.gifts * reward.rounds).toLocaleString('en-US')} ROUNDS${totalWorth ? ` · ${totalWorth} TOTAL` : ''}`)
        );
      } else if (milestone) {
        ticket.append(
          el('span', 'pa-builder__unlock-mark', String(milestone)),
          el('span', 'pa-builder__unlock-kicker', 'MILESTONE UNLOCKED'),
          el('strong', '', `${next.gifts} FREE ${rewardName()} BOXES`),
          el('b', 'pa-builder__unlock-rounds', `${(next.gifts * reward.rounds).toLocaleString('en-US')} FREE ROUNDS${totalWorth ? ` · ${totalWorth} OF FREE ${rewardName()}` : ''}`),
          el('small', '', `+${giftDelta} ${boxWord(giftDelta)} JUST ADDED · NEXT MILESTONE AT BOX #${nextMilestoneAfter(milestone)}`)
        );
      } else {
        const addedWorth = rewardWorth(giftDelta);
        ticket.append(
          el('span', 'pa-builder__unlock-mark', '✓'),
          el('span', 'pa-builder__unlock-kicker', 'REWARD UNLOCKED'),
          el('strong', '', `+${giftDelta} FREE ${rewardName()} ${boxWord(giftDelta)}`),
          el('b', 'pa-builder__unlock-rounds', `${(giftDelta * reward.rounds).toLocaleString('en-US')} FREE ROUNDS${addedWorth ? ` · ${addedWorth} VALUE` : ' ADDED'}`),
          el('small', '', `${next.gifts} FREE ${boxWord(next.gifts)} · ${(next.gifts * reward.rounds).toLocaleString('en-US')} ROUNDS${totalWorth ? ` · ${totalWorth} TOTAL` : ' TOTAL'}`)
        );
      }
      anchor.append(ticket);
      celebrationLayer.append(anchor);
    };
    const celebrateReward = (transition) => {
      if (!transition || transition.giftDelta <= 0) return;
      clearRewardCelebration();
      const { giftDelta, next, previous } = transition;
      const tier = tierCrossed(previous.subtotal, next.subtotal);
      const milestone = tier ? 0 : milestoneCrossed(previous.gifts, next.gifts);
      const bigMoment = Boolean(tier || milestone);
      buzz(bigMoment ? [45, 70, 45, 70, 140] : 40);
      const center = celebrationCenter();
      appendUnlockTicket(giftDelta, next, center, milestone, tier);
      const mobileCelebration = center.width < 750;
      const pieces = mobileCelebration ? 72 : 96;
      appendConfettiBurst(
        center.x,
        center.y,
        bigMoment ? Math.round(pieces * 1.6) : pieces,
        Math.min(center.width * (mobileCelebration ? .78 : .44), mobileCelebration ? 330 : 600) * (bigMoment ? 1.2 : 1),
        'center',
        giftDelta,
        bigMoment
      );
      void root.offsetWidth;
      root.classList.add('pa-builder--reward-celebrating');
      root.classList.toggle('pa-builder--milestone-celebrating', bigMoment);
      celebrationTimer = window.setTimeout(clearRewardCelebration, bigMoment ? 3200 : 2400);
    };
    const pulseReward = (previous, next) => {
      clearRewardPulse();
      if (reducedMotion.matches) return;
      const fills = [root.querySelector('[data-pa-shop-progress]'), root.querySelector('[data-pa-wallet-fill]')].filter(Boolean);
      fills.forEach((fill) => {
        fill.style.transition = 'none';
        fill.style.width = `${previous.percent}%`;
      });
      void root.offsetWidth;
      fills.forEach((fill) => {
        fill.style.removeProperty('transition');
        fill.style.width = '100%';
      });
      root.classList.add('pa-builder--reward-hit');
      rewardSettleTimer = window.setTimeout(() => {
        fills.forEach((fill) => { fill.style.width = `${next.percent}%`; });
      }, 430);
      rewardPulseTimer = window.setTimeout(clearRewardPulse, 1100);
    };
    const persistFeedback = () => {
      try { sessionStorage.setItem('pa-builder-feedback-v2', JSON.stringify({ lastMutation, lastReward })); } catch (_) {}
    };
    const rewardSpeech = (totals) => totals.gifts
      ? `${totals.gifts} free ${reward.label} ${totals.gifts === 1 ? 'box is' : 'boxes are'} unlocked, ${(totals.gifts * reward.rounds).toLocaleString('en-US')} free rounds total. ${money(totals.remaining)} to free box ${totals.gifts + 1}.`
      : `${money(totals.remaining)} to your first free ${reward.rounds}-round ${reward.label} box.`;
    const commitSelections = (key, nextSelections) => {
      const product = products.get(key);
      if (!product || !selectionLedger) return null;
      const previousQuantity = basket.get(key) || 0;
      const previous = deriveBasketTotals();
      const normalized = selectionLedger.normalize(nextSelections, limits);
      selections = normalized;
      rebuildBasket();
      const nextQuantity = basket.get(key) || 0;
      if (nextQuantity === previousQuantity) return null;
      clearRewardPulse();
      selectionVersion += 1;
      if (checkoutStatus) checkoutStatus.textContent = '';
      persistSelections();
      const next = deriveBasketTotals();
      return {
        key,
        product,
        previous,
        next,
        previousQuantity,
        nextQuantity,
        quantityDelta: nextQuantity - previousQuantity,
        giftDelta: next.gifts - previous.gifts
      };
    };
    const finishSelectionChange = (transition, selectionId = '') => {
      if (!transition) return null;
      const { key, product, previous, next, previousQuantity, nextQuantity, quantityDelta, giftDelta } = transition;
      const changedAmount = productAmount(product, Math.abs(quantityDelta));
      const totalAmount = productAmount(product, nextQuantity);
      const totalVerb = productUnitCount(product, nextQuantity) === 1 ? 'is' : 'are';

      lastMutation = quantityDelta > 0 ? {
        key,
        selectionId,
        quantityAfter: nextQuantity,
        quantityDelta,
        unitPrice: product.price,
        threshold,
        amount: changedAmount,
        giftDelta: Math.max(0, giftDelta),
        subtotal: next.subtotal,
        gifts: next.gifts,
        rounds: next.gifts * reward.rounds,
        remaining: next.remaining,
        timestamp: Date.now()
      } : null;
      if (giftDelta > 0) {
        lastReward = {
          key,
          title: product.title,
          amount: changedAmount,
          giftDelta,
          gifts: next.gifts,
          rounds: next.gifts * reward.rounds,
          remaining: next.remaining,
          timestamp: Date.now()
        };
      } else if (giftDelta < 0 || (lastReward && (lastReward.gifts > next.gifts || quantityDelta < 0 && lastReward.key === key))) {
        lastReward = null;
      }
      persistFeedback();
      crateDrop = Math.max(0, giftDelta);
      render(next);
      if (giftDelta > 0) {
        pulseReward(previous, next);
        celebrateReward(transition);
      } else if (giftDelta < 0) {
        clearRewardCelebration();
      }
      let announcement;
      if (!nextQuantity) {
        const rewardLoss = Math.max(0, previous.gifts - next.gifts);
        const removeResult = `Removed ${product.title} from your loadout. Loadout total ${money(next.subtotal)}.`;
        if (rewardLoss) {
          announcement = `Reward updated. ${rewardLoss} free ${reward.label} ${rewardLoss === 1 ? 'box was' : 'boxes were'} removed; ${next.gifts} ${next.gifts === 1 ? 'box remains' : 'boxes remain'}. ${removeResult} ${rewardSpeech(next)}`;
        } else {
          announcement = `${removeResult} ${rewardSpeech(next)}`;
        }
      } else if (quantityDelta > 0) {
        const addResult = `Added ${changedAmount} of ${product.title}. ${totalAmount} of this item ${totalVerb} in your loadout. Loadout total ${money(next.subtotal)}.`;
        if (giftDelta > 0) {
          announcement = `Reward unlocked: ${giftDelta} new free ${reward.label} ${giftDelta === 1 ? 'box' : 'boxes'}, ${(giftDelta * reward.rounds).toLocaleString('en-US')} free rounds. ${addResult} ${rewardSpeech(next)}`;
        } else {
          announcement = `${addResult} ${rewardSpeech(next)}`;
        }
      } else {
        const rewardLoss = Math.max(0, previous.gifts - next.gifts);
        const removeResult = `Removed ${changedAmount} of ${product.title}. ${totalAmount} remain in your loadout. Loadout total ${money(next.subtotal)}.`;
        if (rewardLoss) {
          announcement = `Reward updated. ${rewardLoss} free ${reward.label} ${rewardLoss === 1 ? 'box was' : 'boxes were'} removed; ${next.gifts} ${next.gifts === 1 ? 'box remains' : 'boxes remain'}. ${removeResult} ${rewardSpeech(next)}`;
        } else {
          announcement = `${removeResult} ${rewardSpeech(next)}`;
        }
      }
      announceStatus(announcement);
      return transition;
    };
    const addSelection = (key, quantity) => {
      const product = products.get(key);
      const count = Math.floor(Number(quantity));
      if (!product || !selectionLedger || !Number.isSafeInteger(count) || count <= 0) return null;
      const id = nextSelectionId();
      const nextSelections = selectionLedger.add(selections, { id, key, quantity: count }, product.max);
      return finishSelectionChange(commitSelections(key, nextSelections), id);
    };
    const removeSelection = (id) => {
      const selection = selections.find((item) => item.id === id);
      if (!selection || !selectionLedger) return null;
      return finishSelectionChange(commitSelections(selection.key, selectionLedger.remove(selections, id)), id);
    };
    const selectionRowsForDisplay = (items) => {
      if (selectionLedger && typeof selectionLedger.groupForDisplay === 'function') {
        return selectionLedger.groupForDisplay(items, 1);
      }
      return items.map((item) => ({
        type: 'selection',
        key: item.key,
        quantity: item.quantity,
        count: 1,
        selections: [item]
      }));
    };
    const repeatSelectionStepper = (product, displayRow) => {
      const preset = repeatPresetCopy(product, displayRow.quantity, displayRow.count);
      const countVerb = displayRow.count === 1 ? 'is' : 'are';
      const currentQuantity = basket.get(product.key) || 0;
      const remainingQuantity = Math.max(0, product.max - currentQuantity);
      const canIncrement = displayRow.quantity <= remainingQuantity;
      const stepper = el('div', 'pa-builder__selection-stepper');
      stepper.setAttribute('role', 'group');
      stepper.setAttribute('aria-label', `${displayRow.count} ${preset.countLabel} of ${product.title}, ${preset.total} total`);
      const decrement = el('button', 'pa-builder__selection-stepper-button is-decrement', '−');
      decrement.type = 'button';
      decrement.dataset.paRemoveSelection = displayRow.selections[displayRow.selections.length - 1].id;
      decrement.dataset.paRepeatKey = product.key;
      decrement.dataset.paRepeatQuantity = String(displayRow.quantity);
      decrement.setAttribute('aria-label', `Remove one ${preset.singular} of ${product.title}. ${displayRow.count} ${preset.countLabel} ${countVerb} in your loadout.`);
      const count = el('span', 'pa-builder__selection-stepper-count', String(displayRow.count));
      count.setAttribute('aria-hidden', 'true');
      const increment = el('button', 'pa-builder__selection-stepper-button is-increment', '+');
      increment.type = 'button';
      increment.dataset.paAddRepeat = '';
      increment.dataset.paRepeatKey = product.key;
      increment.dataset.paRepeatQuantity = String(displayRow.quantity);
      increment.setAttribute('aria-disabled', String(!canIncrement));
      const unavailableMessage = remainingQuantity
        ? `Cannot add another ${preset.singular} of ${product.title}. Only ${productAmount(product, remainingQuantity)} remain; choose a smaller amount.`
        : `Maximum stock reached for ${product.title}. ${displayRow.count} ${preset.countLabel} ${countVerb} in your loadout.`;
      increment.dataset.paUnavailableMessage = canIncrement ? '' : unavailableMessage;
      increment.setAttribute('aria-label', canIncrement
        ? `Add another ${preset.singular} of ${product.title}. ${displayRow.count} ${preset.countLabel} ${countVerb} currently in your loadout.`
        : unavailableMessage);
      stepper.append(decrement, count, increment);
      return stepper;
    };

    function showStage(index, scroll = true, focusHeading = false) {
      stageIndex = Math.max(0, Math.min(stages.length - 1, index));
      const stage = stages[stageIndex];
      if (stage.key !== 'cart') lastShopStage = stageIndex;
      root.querySelector('[data-pa-stage-eyebrow]').textContent = stage.eyebrow;
      const heading = root.querySelector('[data-pa-stage-title]');
      setRewardCopy(heading, stage.title);
      setRewardCopy(root.querySelector('[data-pa-stage-description]'), stage.description);
      root.querySelector('[data-pa-shop-panel]').hidden = stage.key === 'cart';
      root.querySelector('[data-pa-cart-panel]').hidden = stage.key !== 'cart';
      root.classList.toggle('pa-builder--cart', stage.key === 'cart');
      root.classList.toggle('pa-builder--popular', stage.key === 'popular');
      root.classList.toggle('pa-builder--specialty', stage.key === 'specialty');
      root.classList.toggle('pa-builder--cans', stage.key === 'cans');
      root.classList.toggle('pa-builder--targets', stage.key === 'targets');
      root.querySelector('[data-pa-mobile-bar]').hidden = stage.key === 'cart';
      const mobileNext = root.querySelector('[data-pa-mobile-next]');
      if (mobileNext && stage.nextDestination) {
        mobileNext.querySelector('[data-pa-mobile-next-kicker]').textContent = stage.nextKicker;
        mobileNext.querySelector('[data-pa-mobile-next-destination]').textContent = stage.nextShort || stage.nextDestination;
        mobileNext.setAttribute('aria-label', stage.nextLabel);
      }
      root.querySelector('[data-pa-actions]').hidden = stage.key === 'cart';
      const actionReview = root.querySelector('[data-pa-action-review]');
      if (actionReview) actionReview.hidden = !basket.size || stage.key === 'cans';
      const backButton = root.querySelector('[data-pa-back]');
      backButton.hidden = stageIndex === 0;
      if (stage.backLabel) {
        const backDestination = stage.backLabel.replace(/^BACK:\s*/i, '');
        backButton.querySelector('[data-pa-back-label]').textContent = backDestination;
        backButton.setAttribute('aria-label', `Back to ${backDestination.toLowerCase()}`);
      }
      if (stage.nextLabel) root.querySelector('[data-pa-continue-label]').textContent = stage.nextLabel;
      root.querySelectorAll('[data-pa-step]').forEach((button) => {
        if (button.dataset.paStep === stage.key) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
      });
      const stickyRewardProgress = root.querySelector('[data-pa-reward-progress]');
      if (stage.key === 'cart') stickyRewardProgress.setAttribute('aria-hidden', 'true');
      else stickyRewardProgress.removeAttribute('aria-hidden');
      cards.forEach((card) => { card.hidden = card.dataset.category !== stage.key; });
      if (scroll) {
        requestAnimationFrame(() => {
          heading.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
          if (focusHeading) heading.focus({ preventScroll: true });
        });
      } else if (focusHeading) heading.focus({ preventScroll: true });
    }

    function render(totals = deriveBasketTotals()) {
      const { subtotal, gifts, progress, remaining, productCount, bonusGifts, bonusTier, nextTier, nextStepGain } = totals;
      const selectionCount = selections.length;
      // What the shopper is getting, e.g. "1,600 rounds · 25 targets · 2 cans".
      const loadoutAmounts = [...basket].reduce((sum, [key, quantity]) => {
        const product = products.get(key);
        if (product.rounds) sum.rounds += quantity * product.rounds;
        else if (product.targets) sum.targets += quantity * product.targets;
        else sum.cans += quantity;
        return sum;
      }, { rounds: 0, targets: 0, cans: 0 });
      const loadoutAmountLabel = [
        loadoutAmounts.rounds && `${loadoutAmounts.rounds.toLocaleString('en-US')} rounds`,
        loadoutAmounts.targets && `${loadoutAmounts.targets.toLocaleString('en-US')} ${loadoutAmounts.targets === 1 ? 'target' : 'targets'}`,
        loadoutAmounts.cans && `${loadoutAmounts.cans} ${loadoutAmounts.cans === 1 ? 'can' : 'cans'}`
      ].filter(Boolean).join(' · ');
      const mutationProduct = lastMutation && products.get(lastMutation.key);
      const mutationIsCurrent = Boolean(
        lastMutation &&
        mutationProduct &&
        basket.get(lastMutation.key) === lastMutation.quantityAfter &&
        Number.isInteger(lastMutation.quantityDelta) &&
        lastMutation.quantityDelta > 0 &&
        lastMutation.unitPrice === mutationProduct.price &&
        lastMutation.threshold === threshold &&
        lastMutation.subtotal === subtotal &&
        lastMutation.gifts === gifts &&
        lastMutation.rounds === gifts * reward.rounds &&
        lastMutation.remaining === remaining &&
        lastMutation.amount === productAmount(mutationProduct, lastMutation.quantityDelta)
      );
      if (lastMutation && !mutationIsCurrent) {
        lastMutation = null;
        persistFeedback();
      }
      if (lastReward && lastReward.gifts > gifts) {
        lastReward = null;
        persistFeedback();
      }
      if (checkoutButton) {
        checkoutButton.disabled = !basket.size || syncingCheckout;
        if (!syncingCheckout) {
          checkoutButton.querySelector('[data-pa-checkout-label]').textContent = gifts
            ? `CHECKOUT WITH ${gifts} FREE ${gifts === 1 ? 'BOX' : 'BOXES'}`
            : 'CONTINUE TO CHECKOUT';
        }
      }
      root.querySelector('[data-pa-total]').textContent = money(subtotal);
      root.querySelector('[data-pa-merchandise-total]').textContent = money(subtotal);
      root.querySelector('[data-pa-summary-reward-label]').textContent = gifts
        ? `FREE ${rewardName()} · ${gifts} × ${reward.rounds} RDS`
        : `FREE ${rewardName()} REWARD`;
      const summaryReward = root.querySelector('[data-pa-summary-reward]');
      if (gifts && rewardValue) {
        summaryReward.replaceChildren(el('s', '', money(gifts * rewardValue)), document.createTextNode(' FREE'));
      } else {
        summaryReward.textContent = gifts ? `${gifts} ${boxWord(gifts)} · FREE` : `${compactMoney(remaining)} TO FIRST FREE BOX`;
      }
      root.querySelector('[data-pa-action-total]').textContent = money(subtotal);
      const actionReward = root.querySelector('[data-pa-action-summary]');
      actionReward.textContent = gifts
        ? `${gifts} FREE ${boxWord(gifts)}${rewardValue ? ` · ${rewardWorth(gifts)} VALUE` : ''}`
        : productCount ? `${compactMoney(remaining)} TO FIRST FREE BOX` : 'EVERY $100 = 1 FREE BOX';
      actionReward.classList.toggle('is-earned', gifts > 0);
      root.querySelector('[data-pa-cart-selection-count]').textContent = loadoutAmountLabel;
      root.querySelector('[data-pa-mobile-total]').textContent = `LOADOUT · ${money(subtotal)}`;
      const mobileReward = root.querySelector('[data-pa-mobile]');
      mobileReward.textContent = gifts
        ? `${gifts} FREE${rewardValue ? ` · ${rewardWorth(gifts)} VALUE` : ` ${boxWord(gifts)}`}`
        : productCount ? `${compactMoney(remaining)} TO YOUR FIRST FREE BOX` : `START UNLOCKING FREE ${rewardName()}`;
      mobileReward.classList.toggle('is-free-reward', gifts > 0);
      mobileReward.classList.toggle('is-reward-progress', productCount > 0 && gifts === 0);

      const rewardTrack = root.querySelector('[data-pa-reward-progress]');
      const nextBox = gifts + 1;
      const nextStepAt = subtotal + remaining;
      const loadedPercent = Math.max(1, Math.floor(progress / threshold * 100));
      // Meter states: "momentum" right after an add unlocked a box, "bonus" while the next $100 crossing
      // lands on a bonus tier, "near" within nearThreshold of the next box (the whole segment for a bonus).
      const isMomentum = Boolean(lastMutation && mutationIsCurrent && lastMutation.giftDelta > 0);
      const isBonusSegment = productCount > 0 && Boolean(bonusTier);
      const isNear = productCount > 0 && progress > 0 && remaining <= (isBonusSegment ? threshold : nearThreshold);
      const isAnticipating = Boolean(productCount > 0 && nextTier && !isBonusSegment && nextTier.at - subtotal <= threshold * 2);
      const tierLabel = bonusTier ? compactMoney(bonusTier.at) : '';
      const totalWorth = rewardWorth(gifts);
      const eachWorth = rewardWorth(1);
      root.classList.toggle('pa-builder--has-rewards', gifts > 0);
      root.classList.toggle('pa-builder--no-rewards', gifts === 0);
      root.classList.toggle('pa-builder--reward-near', isNear);
      root.classList.toggle('pa-builder--reward-bonus', isBonusSegment);
      root.classList.toggle('pa-builder--reward-momentum', isMomentum);
      renderCrate(root.querySelector('[data-pa-crate]'), gifts, compactCrate.matches ? 4 : 5, crateDrop, productCount ? nextStepGain : 1);
      root.querySelector('[data-pa-reward-unit]').textContent = gifts
        ? `${gifts} FREE ${rewardName()} ${boxWord(gifts)}`
        : 'NO FREE BOXES YET';
      root.querySelector('[data-pa-reward-rounds]').textContent = gifts
        ? `${(gifts * reward.rounds).toLocaleString('en-US')} FREE ROUNDS${totalWorth ? ` · ${totalWorth} VALUE` : ''}`
        : `NEXT REWARD · ${reward.rounds} FREE ROUNDS`;
      root.querySelector('[data-pa-reward-status]').textContent = isMomentum
        ? progress ? `BOX #${nextBox} ALREADY ${loadedPercent}% LOADED` : `FREE BOX #${gifts} SECURED`
        : isBonusSegment
          ? `${remaining <= nearThreshold ? 'ONLY ' : ''}${compactMoney(remaining)} TO THE ${tierLabel} BONUS`
          : isNear
            ? `ONLY ${compactMoney(remaining)} TO FREE BOX #${nextBox}`
            : `${compactMoney(remaining)} TO FREE BOX #${nextBox}`;
      root.querySelector('[data-pa-reward-next-label]').textContent = isMomentum
        ? progress ? `FREE BOX #${gifts} SECURED` : isBonusSegment ? `${tierLabel} UNLOCKS ${nextStepGain} FREE BOXES AT ONCE` : `NEXT BOX AT ${compactMoney(nextStepAt)}`
        : isBonusSegment
          ? `${tierLabel} UNLOCKS ${nextStepGain} FREE BOXES AT ONCE`
          : isNear
            ? `ALMOST THERE · +${reward.rounds} FREE ROUNDS`
            : `${gifts ? '+' : 'UNLOCKS '}${reward.rounds} FREE ${rewardName()} ROUNDS${eachWorth ? ` · ${eachWorth} VALUE` : ''}`;
      const tiersLine = bonusTiers.length ? ` · BONUS BOXES AT ${bonusTiers.map((tier) => compactMoney(tier.at)).join(' + ')}` : '';
      // The compact meter has no count column, so its single line carries the running value instead.
      root.querySelector('[data-pa-reward-activity]').textContent = isAnticipating
        ? `NEXT BONUS AT ${compactMoney(nextTier.at)} · ${compactMoney(nextTier.at - subtotal)} TO GO · +${nextTier.bonus} BONUS ${boxWord(nextTier.bonus)}`
        : isBonusSegment
          ? `HIT ${tierLabel} → ${nextStepGain} FREE BOXES AT ONCE (+${bonusTier.bonus} BONUS)`
          : !gifts
            ? `EVERY $100 = 1 FREE BOX${tiersLine}`
            : compactCrate.matches
              ? `${gifts} FREE ${boxWord(gifts)} · ${(gifts * reward.rounds).toLocaleString('en-US')} FREE ROUNDS${totalWorth ? ` · ${totalWorth} OF FREE ${rewardName()}` : ''}`
              : nextTier
                ? `EVERY $100 = 1 MORE FREE BOX · NEXT BONUS AT ${compactMoney(nextTier.at)}`
                : 'EVERY $100 = 1 MORE FREE BOX · NO LIMIT';
      const gapCloser = root.querySelector('[data-pa-gap-closer]');
      const gapChoice = isNear ? findGapCloser(remaining) : null;
      root.querySelector('[data-pa-reward-activity]').hidden = Boolean(gapChoice);
      gapCloser.hidden = !gapChoice;
      if (gapChoice) {
        const gapAmount = productAmount(gapChoice.product, gapChoice.quantity);
        gapCloser.dataset.paGapKey = gapChoice.product.key;
        gapCloser.dataset.paGapQuantity = String(gapChoice.quantity);
        gapCloser.querySelector('[data-pa-gap-lead]').textContent = isBonusSegment ? `UNLOCK ${nextStepGain} BOXES:` : `UNLOCK BOX #${nextBox}:`;
        gapCloser.querySelector('[data-pa-gap-item]').textContent = `+ ${gapChoice.product.title}`;
        gapCloser.querySelector('[data-pa-gap-price]').textContent = compactMoney(gapChoice.cost);
        gapCloser.setAttribute('aria-label', isBonusSegment
          ? `Add ${gapAmount} of ${gapChoice.product.title} for ${money(gapChoice.cost)} to reach ${tierLabel} and unlock ${nextStepGain} free boxes`
          : `Add ${gapAmount} of ${gapChoice.product.title} for ${money(gapChoice.cost)} to unlock free box ${nextBox}`);
      }
      const rewardFill = root.querySelector('[data-pa-shop-progress]');
      rewardFill.style.width = `${totals.percent}%`;
      rewardTrack.setAttribute('aria-valuenow', String(Number(totals.percent.toFixed(2))));
      rewardTrack.setAttribute('aria-valuetext', gifts
        ? `${compactMoney(progress)} of ${compactMoney(threshold)} toward free box ${nextBox}; ${gifts} ${gifts === 1 ? 'box' : 'boxes'} unlocked, ${(gifts * reward.rounds).toLocaleString('en-US')} free rounds total${totalWorth ? `, ${totalWorth} value` : ''}`
        : `${compactMoney(progress)} of ${compactMoney(threshold)} toward your first free ${reward.rounds}-round ${reward.label} box`);

      root.querySelector('[data-pa-wallet-title]').textContent = gifts
        ? `${gifts} FREE ${rewardName()} ${boxWord(gifts)} UNLOCKED`
        : `UNLOCK YOUR FIRST FREE ${rewardName()} BOX`;
      // Before the first box, explain the rule; after it, the title and rounds line already say it.
      const walletCopy = root.querySelector('[data-pa-wallet-copy]');
      walletCopy.textContent = `Every $100 in your loadout unlocks ${reward.rounds} FREE ${reward.label} rounds.`;
      walletCopy.hidden = gifts > 0;
      root.querySelector('[data-pa-wallet-rounds]').textContent = gifts
        ? `${(gifts * reward.rounds).toLocaleString('en-US')} FREE ROUNDS${totalWorth ? ` · ${totalWorth} VALUE` : ' INCLUDED'}`
        : `NEXT REWARD · ${reward.rounds} FREE ROUNDS`;
      root.querySelector('[data-pa-wallet-next]').textContent = isBonusSegment
        ? `${compactMoney(remaining)} TO THE ${tierLabel} BONUS`
        : gifts
          ? `${compactMoney(remaining)} TO FREE BOX #${nextBox}`
          : `${compactMoney(remaining)} TO UNLOCK`;
      root.querySelector('[data-pa-wallet-threshold]').textContent = isBonusSegment
        ? `UNLOCKS ${nextStepGain} FREE BOXES AT ONCE`
        : nextTier && gifts
          ? `NEXT BONUS AT ${compactMoney(nextTier.at)} · +${nextTier.bonus} BONUS ${boxWord(nextTier.bonus)}`
          : gifts ? `+${reward.rounds} FREE ${rewardName()} ROUNDS` : `FREE BOX #1 UNLOCKS AT ${compactMoney(threshold)}`;
      const walletProgress = root.querySelector('[data-pa-wallet-progress]');
      root.querySelector('[data-pa-wallet-fill]').style.width = `${totals.percent}%`;
      walletProgress.setAttribute('aria-valuenow', String(Number(totals.percent.toFixed(2))));
      walletProgress.setAttribute('aria-valuetext', rewardTrack.getAttribute('aria-valuetext'));
      renderCrate(root.querySelector('[data-pa-reward-rack]'), gifts, 50, crateDrop, productCount ? nextStepGain : 1);
      crateDrop = 0;

      const productCounts = [...basket.keys()].reduce((counts, key) => {
        const category = products.get(key)?.category;
        if (category) counts[category] = (counts[category] || 0) + 1;
        return counts;
      }, {});
      root.querySelectorAll('[data-pa-step]').forEach((button) => {
        const step = button.dataset.paStep;
        const count = step === 'cart' ? basket.size : productCounts[step] || 0;
        const label = button.querySelector('.pa-builder__step-label').textContent;
        button.classList.toggle('is-filled', count > 0);
        button.querySelector('[data-pa-step-badge]').textContent = count ? step === 'cart' ? String(count) : '✓' : '';
        if (count) button.setAttribute('aria-label', `${label}, ${count} ${count === 1 ? 'item' : 'items'} in your loadout`);
        else button.removeAttribute('aria-label');
      });
      root.querySelector('[data-pa-mobile-bar]').hidden = stages[stageIndex].key === 'cart';
      root.classList.toggle('pa-builder--empty', !basket.size);
      const actionReview = root.querySelector('[data-pa-action-review]');
      if (actionReview) actionReview.hidden = !basket.size || stages[stageIndex].key === 'cans';

      cards.forEach((card) => {
        const selected = card.querySelector('[data-pa-selected]');
        if (!selected) return;
        const product = products.get(card.dataset.key);
        if (!product) {
          card.hidden = true;
          return;
        }
        const quantity = basket.get(card.dataset.key) || 0;
        const productSelections = selections.filter((item) => item.key === product.key);
        card.classList.toggle('pa-builder__product--in-cart', quantity > 0);
        const receipt = card.querySelector('[data-pa-product-receipt]');
        const hasReceipt = Boolean(lastMutation && lastMutation.key === product.key && lastMutation.amount);
        const cardSubtotal = quantity * product.price;
        const cardGifts = Math.floor(cardSubtotal / threshold);
        if (receipt) {
          const showReceipt = hasReceipt && (cardGifts > 0 || lastMutation.giftDelta > 0);
          receipt.hidden = !showReceipt;
          receipt.classList.toggle('is-reward', showReceipt);
          if (showReceipt) {
            const isPooledUnlock = cardGifts === 0 && lastMutation.giftDelta > 0;
            const receiptGifts = isPooledUnlock ? lastMutation.giftDelta : cardGifts;
            const sourceLabel = isPooledUnlock
              ? 'JUST UNLOCKED'
              : product.category === 'targets'
                ? 'THESE TARGETS UNLOCK'
                : product.category === 'cans'
                  ? quantity === 1 ? 'YOUR ENGRAVED CAN UNLOCKS' : 'THESE ENGRAVED CANS UNLOCK'
                  : 'THIS AMMO UNLOCKS';
            receipt.setAttribute('aria-label', `${product.title}: ${receiptGifts} free ${reward.label} ${receiptGifts === 1 ? 'box' : 'boxes'}, ${(receiptGifts * reward.rounds).toLocaleString('en-US')} free rounds`);
            receipt.querySelector('[data-pa-receipt-kicker]').textContent = sourceLabel;
            receipt.querySelector('[data-pa-receipt-title]').textContent = `${isPooledUnlock ? '+' : ''}${receiptGifts} FREE ${rewardName()} ${receiptGifts === 1 ? 'BOX' : 'BOXES'}`;
            const receiptDetails = receipt.querySelector('[data-pa-receipt-details]');
            const facts = isPooledUnlock
              ? [
                `${lastMutation.amount} added`,
                `${(receiptGifts * reward.rounds).toLocaleString('en-US')} FREE ${rewardName()} ROUNDS`
              ]
              : [
                `${productAmount(product, quantity)} in your loadout`,
                `${(receiptGifts * reward.rounds).toLocaleString('en-US')} FREE ${rewardName()} ROUNDS`
              ];
            receiptDetails.replaceChildren(...facts.map((fact) => el('li', '', fact)));
          }
        }
        const choiceLabel = card.querySelector('[data-pa-choices-label]');
        const selectionList = card.querySelector('[data-pa-product-selections]');
        const addButtons = [...card.querySelectorAll('[data-pa-add]')];
        addButtons.forEach((button) => {
          const choiceQuantity = Number(button.dataset.paAdd);
          const incrementCost = choiceQuantity * product.price;
          const candidateSubtotal = subtotal + incrementCost;
          const candidateGifts = giftsAt(candidateSubtotal);
          const candidateProgress = candidateSubtotal % threshold;
          const candidatePercent = Math.min(99, Math.round(candidateProgress / threshold * 100));
          const candidateTargetBox = candidateGifts + 1;
          const giftDelta = candidateGifts - gifts;
          const candidateTier = tierCrossed(subtotal, candidateSubtotal);
          const remainingQuantity = Math.max(0, product.max - quantity);
          button.disabled = choiceQuantity > remainingQuantity;
          const impact = button.querySelector('[data-pa-choice-impact]');
          const next = button.querySelector('[data-pa-choice-next]');
          const action = button.querySelector('[data-pa-choice-action]');
          const amountLabel = button.dataset.paAmountLabel;
          if (action) action.textContent = `ADD ${amountLabel.toUpperCase()}`;
          if (impact && button.disabled) {
            impact.classList.remove('is-bonus');
            impact.classList.remove('is-building-reward');
            impact.textContent = remainingQuantity
              ? `ONLY ${productAmount(product, remainingQuantity).toUpperCase()} LEFT · CHOOSE LESS`
              : 'ALL AVAILABLE STOCK IS IN YOUR LOADOUT';
            if (next) next.hidden = true;
          } else if (impact) {
            impact.classList.toggle('is-bonus', giftDelta > 0);
            impact.classList.toggle('is-building-reward', giftDelta === 0);
            // "+" is reserved for what this pick adds; progress is stated as the level the meter reaches.
            if (giftDelta > 0) {
              const addedWorth = rewardWorth(giftDelta);
              impact.textContent = `+${giftDelta} FREE ${rewardName()} ${boxWord(giftDelta)}${addedWorth ? ` · ${addedWorth} VALUE` : ''}`;
              if (next) {
                next.hidden = !candidateTier;
                next.textContent = candidateTier
                  ? `INCLUDES THE ${compactMoney(candidateTier.at)} BONUS · +${candidateTier.bonus} ${boxWord(candidateTier.bonus)}`
                  : candidateProgress
                    ? `BOX #${candidateTargetBox} STARTS ${candidatePercent}% LOADED`
                    : '';
                next.classList.toggle('is-tier', Boolean(candidateTier));
              }
            } else {
              impact.textContent = `LOADS FREE BOX #${candidateTargetBox} TO ${candidatePercent}%`;
              if (next) {
                next.hidden = true;
                next.textContent = '';
              }
            }
          }
          button.setAttribute('aria-label', button.disabled
            ? remainingQuantity
              ? `Cannot add ${amountLabel} of ${product.title}. Only ${productAmount(product, remainingQuantity)} remain.`
              : `Cannot add more ${product.title}. All available inventory is already in your loadout.`
            : giftDelta > 0
              ? `Add ${amountLabel} of ${product.title} for ${money(incrementCost)}. Unlocks ${giftDelta} free ${reward.label} ${giftDelta === 1 ? 'box' : 'boxes'}; reward total becomes ${candidateGifts} ${candidateGifts === 1 ? 'box' : 'boxes'}${candidateProgress ? `, with ${candidatePercent} percent toward free box ${candidateTargetBox}` : ''}.`
              : `Add ${amountLabel} of ${product.title} for ${money(incrementCost)}. Brings free box ${candidateTargetBox} to ${candidatePercent} percent.`);
        });
        if (choiceLabel) {
          const isTargets = product.category === 'targets';
          const isCans = product.category === 'cans';
          choiceLabel.textContent = quantity
            ? isTargets ? 'ADD MORE TARGETS' : isCans ? 'ADD MORE CANS' : 'ADD MORE AMMO'
            : isTargets ? 'CHOOSE A TARGET PACK' : isCans ? 'CHOOSE A CAN' : 'CHOOSE YOUR AMMO AMOUNT';
          choiceLabel.hidden = false;
        }
        selected.hidden = !quantity;
        const rounds = Number(card.dataset.rounds) || 0;
        const targets = Number(card.dataset.targets) || 0;
        const amountInCart = rounds
          ? `${(quantity * rounds).toLocaleString('en-US')} ROUNDS`
          : targets ? `${(quantity * targets).toLocaleString('en-US')} TARGETS`
            : `${quantity} ${quantity === 1 ? 'CAN' : 'CANS'}`;
        selected.textContent = quantity
          ? `IN LOADOUT · ${amountInCart}`
          : '';
        if (selectionList) {
          selectionList.hidden = !productSelections.length;
          selectionList.replaceChildren();
          selectionRowsForDisplay(productSelections).forEach((displayRow) => {
            const pick = displayRow.selections[0];
            const row = el('div', 'pa-builder__product-selection');
            row.setAttribute('role', 'listitem');
            row.classList.toggle('is-grouped', displayRow.type === 'group');
            const description = el('div', 'pa-builder__product-selection-detail');
            if (displayRow.type === 'group') {
              row.dataset.paSelectionGroup = String(displayRow.quantity);
              description.append(el('strong', '', repeatPresetCopy(product, displayRow.quantity, displayRow.count).label));
              row.append(description, repeatSelectionStepper(product, displayRow));
              selectionList.append(row);
              return;
            }
            row.dataset.paSelection = pick.id;
            description.append(el('strong', '', productAmount(product, pick.quantity)));
            row.append(description);
            const remove = el('button', 'pa-builder__selection-remove', 'Remove');
            remove.type = 'button';
            remove.dataset.paRemoveSelection = pick.id;
            remove.dataset.paSelectionQuantity = String(pick.quantity);
            remove.setAttribute('aria-label', `Remove ${productAmount(product, pick.quantity)} of ${product.title} from your loadout`);
            row.append(remove);
            selectionList.append(row);
          });
        }
      });

      lines.replaceChildren();
      if (!basket.size) {
        lines.append(el('p', 'pa-builder__empty', `Your loadout is empty. Start with your go-to ammo and begin unlocking FREE ${reward.label}.`));
        const explore = el('button', 'pa-builder__empty-cta', 'START WITH POPULAR AMMO');
        explore.type = 'button';
        explore.dataset.paShopPopular = '';
        lines.append(explore);
      }

      basket.forEach((quantity, key) => {
        const product = products.get(key);
        const productSelections = selections.filter((item) => item.key === key);
        const group = el('article', 'pa-builder__cart-group');
        group.dataset.paLine = key;
        const displayRows = selectionRowsForDisplay(productSelections);
        // A product with one entry (a single add, or one bundle size repeated) needs no second row:
        // its Remove link or stepper sits beside the price.
        const onlyRow = displayRows.length === 1 ? displayRows[0] : null;
        const isSingle = Boolean(onlyRow);
        group.classList.toggle('is-single', isSingle);
        group.setAttribute('aria-label', product.title);
        const header = el('div', 'pa-builder__cart-group-header');
        const media = el('div', 'pa-builder__cart-group-media');
        if (product.image) {
          const image = document.createElement('img');
          image.src = product.image;
          image.alt = '';
          media.append(image);
        }
        const detail = el('div', 'pa-builder__cart-group-detail');
        detail.append(el('strong', '', product.title));
        const bundleNote = onlyRow && onlyRow.type === 'group' && productUnitCount(product, onlyRow.quantity) > 1
          ? ` · ${onlyRow.count} × ${repeatPresetCopy(product, onlyRow.quantity, 1).singular}`
          : '';
        detail.append(el('span', '', `${productAmount(product, quantity)}${bundleNote}`));
        // One row: image, name/amount, control (single entry only), price.
        const control = el('div', 'pa-builder__cart-group-control');
        header.append(media, detail, control, el('strong', 'pa-builder__cart-group-total', money(quantity * product.price)));
        if (isSingle && onlyRow.type === 'group') {
          control.append(repeatSelectionStepper(product, onlyRow));
          group.append(header);
          lines.append(group);
          return;
        }
        if (isSingle) {
          const only = onlyRow.selections[0];
          const remove = el('button', 'pa-builder__cart-pick-remove', 'Remove');
          remove.type = 'button';
          remove.dataset.paRemoveSelection = only.id;
          remove.dataset.paSelectionQuantity = String(only.quantity);
          remove.setAttribute('aria-label', `Remove ${productAmount(product, only.quantity)} of ${product.title} from your loadout`);
          control.append(remove);
          group.append(header);
          lines.append(group);
          return;
        }
        const picks = el('div', 'pa-builder__cart-group-picks');
        picks.setAttribute('role', 'list');
        displayRows.forEach((displayRow) => {
          const pick = displayRow.selections[0];
          const row = el('div', 'pa-builder__cart-pick');
          row.setAttribute('role', 'listitem');
          row.classList.toggle('is-grouped', displayRow.type === 'group');
          const pickDetail = el('div', 'pa-builder__cart-pick-detail');
          if (displayRow.type === 'group') {
            row.dataset.paSelectionGroup = String(displayRow.quantity);
            pickDetail.append(el('strong', '', repeatPresetCopy(product, displayRow.quantity, displayRow.count).label));
            row.append(
              pickDetail,
              repeatSelectionStepper(product, displayRow),
              el('strong', 'pa-builder__cart-pick-price', money(displayRow.quantity * product.price * displayRow.count))
            );
            picks.append(row);
            return;
          }
          row.dataset.paSelection = pick.id;
          pickDetail.append(el('strong', '', productAmount(product, pick.quantity)));
          const remove = el('button', 'pa-builder__cart-pick-remove', 'Remove');
          remove.type = 'button';
          remove.dataset.paRemoveSelection = pick.id;
          remove.dataset.paSelectionQuantity = String(pick.quantity);
          remove.setAttribute('aria-label', `Remove ${productAmount(product, pick.quantity)} of ${product.title} from your loadout`);
          row.append(pickDetail, remove, el('strong', 'pa-builder__cart-pick-price', money(pick.quantity * product.price)));
          picks.append(row);
        });
        group.append(header, picks);
        lines.append(group);
      });
      suggestion.replaceChildren();
      if (!subtotal) return;
      const primary = findGapCloser(remaining);
      if (!primary) return;
      // Offer the closest pick, plus the closest pick in the free-ammo caliber for shoppers who would rather add ammo.
      const nineMm = findGapCloser(remaining, matchesReward);
      const picks = [primary];
      if (nineMm && nineMm.product.key !== primary.product.key) picks.push(nineMm);
      const primaryGiftDelta = giftsAt(subtotal + primary.cost) - gifts;
      const firstUnlockedBox = gifts + 1;
      const option = el('div', 'pa-builder__suggestion-option');
      option.append(el('span', 'pa-builder__suggestion-kicker', primaryGiftDelta > 1
        ? `UNLOCK ${primaryGiftDelta} FREE ${rewardName()} BOXES NOW`
        : gifts ? `ONLY ${compactMoney(remaining)} TO FREE BOX #${firstUnlockedBox}` : `UNLOCK YOUR FIRST FREE ${rewardName()} BOX`));
      picks.forEach((choice, index) => {
        const suggestedSubtotal = subtotal + choice.cost;
        const suggestedGifts = giftsAt(suggestedSubtotal);
        const suggestedGiftDelta = suggestedGifts - gifts;
        const suggestedProgress = suggestedSubtotal % threshold;
        const amount = productAmount(choice.product, choice.quantity);
        const phrase = pickPhrase(choice.product, choice.quantity);
        const lastUnlockedBox = gifts + suggestedGiftDelta;
        const unlockedLabel = suggestedGiftDelta === 1
          ? `FREE BOX #${firstUnlockedBox}`
          : `FREE BOXES #${firstUnlockedBox}–#${lastUnlockedBox}`;
        const pick = el('div', `pa-builder__suggestion-pick${index ? ' is-alt' : ''}`);
        if (index) pick.append(el('span', 'pa-builder__suggestion-alt-label', `OR ADD MORE ${rewardName()}`));
        pick.append(el('strong', '', `Add ${phrase} · ${money(choice.cost)}`));
        pick.append(el('p', '', `Unlocks ${unlockedLabel}${suggestedProgress ? ` and loads ${compactMoney(suggestedProgress)} toward FREE BOX #${suggestedGifts + 1}` : ''}.`));
        const button = el('button', '', `ADD ${amount.toUpperCase()} · UNLOCK ${suggestedGiftDelta} FREE ${boxWord(suggestedGiftDelta)}`);
        button.type = 'button';
        button.setAttribute('aria-label', `Add ${phrase} for ${money(choice.cost)}. Unlocks ${suggestedGiftDelta} free ${reward.rounds}-round ${reward.label} ${suggestedGiftDelta === 1 ? 'box' : 'boxes'}.`);
        button.addEventListener('click', (event) => {
          const transition = addSelection(choice.product.key, choice.quantity);
          if (transition && event.detail === 0) {
            requestAnimationFrame(() => {
              const focusTarget = root.querySelector('[data-pa-suggestion] button')
                || root.querySelector('[data-pa-checkout]:not([disabled])')
                || root.querySelector('[data-pa-keep-building]');
              focusTarget?.focus({ preventScroll: true });
            });
          }
        });
        pick.append(button);
        option.append(pick);
      });
      suggestion.append(option);
    }

    const routeRoot = () => window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
    const goToCheckout = () => window.location.assign(`${routeRoot().replace(/\/?$/, '/')}checkout`);
    // Replaces the builder's lines in the Shopify cart with the current basket plus the free boxes.
    // Shared by the cart checkout button and the loadout cards; throws a shopper-facing message.
    async function syncCartForCheckout({ onProgress } = {}) {
      if (!window.PaBuilderCartSync) throw new Error('Checkout is unavailable right now. Please try again later.');
      const version = selectionVersion;
      const selection = [...basket].map(([key, quantity]) => {
        const product = products.get(key);
        return { key, variant: product.variant, quantity };
      });
      let result;
      try {
        result = await window.PaBuilderCartSync.sync({
          fetcher: window.fetch.bind(window),
          routeRoot: routeRoot(),
          selection,
          threshold,
          bonusTiers,
          expectedGiftQuantity: deriveBasketTotals().gifts,
          ...(onProgress ? { onProgress } : {}),
          ...(Number(reward.variant) ? { giftVariantId: Number(reward.variant) } : {})
        });
      } catch (error) {
        // fetch rejects with a TypeError ("Failed to fetch") when the network drops.
        if (error instanceof TypeError) throw new Error('We could not reach checkout. Check your connection and try again—your loadout is saved.');
        throw error;
      }
      if (selectionVersion !== version) throw new Error('Your cart changed while we checked the reward. Please try again.');
      return result;
    }

    async function checkout() {
      if (!checkoutButton || syncingCheckout || !basket.size) return;
      syncingCheckout = true;
      checkoutButton.disabled = true;
      checkoutButton.querySelector('[data-pa-checkout-label]').textContent = `LOCKING IN YOUR FREE ${rewardName()}`;
      checkoutStatus.textContent = `Adding your loadout and FREE ${reward.label} to checkout…`;
      try {
        const result = await syncCartForCheckout();
        checkoutStatus.textContent = result.giftQuantity
          ? `${result.giftQuantity} FREE ${rewardName()} ${result.giftQuantity === 1 ? 'BOX' : 'BOXES'} CONFIRMED. HEADING TO CHECKOUT…`
          : 'LOADOUT CONFIRMED. HEADING TO CHECKOUT…';
        goToCheckout();
      } catch (error) {
        checkoutStatus.textContent = error && error.message || 'We could not prepare checkout. Your loadout is saved—please try again.';
      } finally {
        syncingCheckout = false;
        const { gifts } = deriveBasketTotals();
        checkoutButton.querySelector('[data-pa-checkout-label]').textContent = gifts
          ? `CHECKOUT WITH ${gifts} FREE ${gifts === 1 ? 'BOX' : 'BOXES'}`
          : 'CONTINUE TO CHECKOUT';
        checkoutButton.disabled = !basket.size;
      }
    }

    // Prebuilt loadouts: fixed baskets priced from the live product cards, so a price change on a
    // product moves every loadout that contains it.
    const loadoutCards = [...root.querySelectorAll('[data-pa-loadout]')];
    const loadoutOverlay = root.querySelector('[data-pa-loadout-overlay]');
    const canKeys = [...products.values()].filter((product) => product.category === 'cans').map((product) => product.key);
    const pickedCans = new Map();
    let loadoutBusy = false;
    let overlayTimers = [];
    const parseLoadout = (value) => String(value || '').split(',')
      .map((entry) => entry.split(':').map((part) => part.trim()))
      .map(([key, quantity]) => ({ key, quantity: Math.floor(Number(quantity)) }))
      .filter((item) => item.key && Number.isSafeInteger(item.quantity) && item.quantity > 0);
    const loadoutFor = (card) => {
      const source = reward.loadout === 'rifle' && card.dataset.itemsRifle ? card.dataset.itemsRifle : card.dataset.items;
      // "can" is the engraving the shopper picks on the card; explicit can keys are fixed contents.
      const picked = pickedCans.get(card) || canKeys.find((key) => products.get(key).max > 0) || '';
      const items = parseLoadout(source).map((item) => item.key === 'can' ? { ...item, key: picked, picked: true } : item);
      const quantities = new Map();
      items.forEach((item) => quantities.set(item.key, (quantities.get(item.key) || 0) + item.quantity));
      const available = quantities.size > 0 && [...quantities].every(([key, quantity]) => products.has(key) && quantity <= products.get(key).max);
      const subtotal = available ? [...quantities].reduce((sum, [key, quantity]) => sum + products.get(key).price * quantity, 0) : 0;
      return {
        card,
        title: card.dataset.title || 'Loadout',
        quantities,
        picked: items.some((item) => item.picked) ? picked : '',
        available,
        subtotal,
        gifts: available ? giftsAt(subtotal) : 0
      };
    };
    // "Birchwood Casey Splatter Targets" -> "Splatter", so two packs read "Splatter + Silhouette".
    const targetName = (product) => product.title.replace(/^Birchwood Casey\s+/i, '').replace(/\s+targets?$/i, '');
    const loadoutLines = (loadout) => {
      const ammo = [];
      const targets = [];
      const cans = [];
      loadout.quantities.forEach((quantity, key) => {
        const product = products.get(key);
        if (!product) return;
        if (product.category === 'cans') cans.push({ product, quantity });
        else if (product.targets) targets.push({ product, quantity });
        else if (product.rounds) ammo.push({ count: quantity * product.rounds, unit: 'RDS', name: product.title });
      });
      const rows = [...ammo];
      // The free boxes are part of the loadout, listed right under the ammo they come with.
      if (loadout.gifts) rows.push({ count: loadout.gifts * reward.rounds, unit: 'RDS', name: reward.name || `${reward.label} ammo`, free: true });
      if (targets.length) {
        rows.push({
          count: targets.reduce((sum, target) => sum + target.quantity * target.product.targets, 0),
          unit: 'TARGETS',
          name: targets.map((target) => targetName(target.product)).join(' + ')
        });
      }
      if (cans.length) {
        const count = cans.reduce((sum, can) => sum + can.quantity, 0);
        rows.push({
          count,
          unit: count === 1 ? 'CAN' : 'CANS',
          name: cans.length === 1 ? `Engraved “${cans[0].product.title}”` : `Engraved · all ${cans.length} designs`
        });
      }
      return rows;
    };
    const loadoutCans = (loadout) => [...loadout.quantities]
      .filter(([key]) => products.get(key)?.category === 'cans')
      .map(([key, quantity]) => ({ key, quantity }));
    const fillCrate = (container, gifts) => {
      if (!container) return;
      const boxes = [];
      for (let slot = 1; slot <= gifts; slot += 1) {
        const box = el('i', 'pa-builder__crate-box');
        if (bonusSlots.has(slot) || isMilestoneBox(slot)) box.classList.add('is-milestone');
        boxes.push(box);
      }
      container.replaceChildren(...boxes);
    };
    // One-can loadouts: pick the engraving. Loadouts with fixed cans: show every included can.
    const renderCanRow = (card, loadout) => {
      const container = card.querySelector('[data-pa-loadout-can]');
      const options = card.querySelector('[data-pa-loadout-can-options]');
      const cans = loadoutCans(loadout);
      const mode = loadout.picked && canKeys.length > 1 ? 'pick' : 'included';
      container.classList.toggle('is-empty', !cans.length);
      const canCount = cans.reduce((sum, can) => sum + can.quantity, 0);
      card.querySelector('[data-pa-loadout-can-label]').textContent = mode === 'pick'
        ? 'PICK YOUR ENGRAVED CAN'
        : canCount === canKeys.length && canCount > 1 ? `ALL ${canCount} ENGRAVED CANS INCLUDED` : `${canCount} ENGRAVED ${canCount === 1 ? 'CAN' : 'CANS'} INCLUDED`;
      const signature = mode === 'pick' ? 'pick' : `included:${cans.map((can) => can.key).join(',')}`;
      if (options.dataset.signature !== signature) {
        options.dataset.signature = signature;
        const keys = mode === 'pick' ? canKeys : cans.map((can) => can.key);
        options.replaceChildren(...keys.map((key) => {
          const product = products.get(key);
          const option = el(mode === 'pick' ? 'button' : 'div', 'pa-builder__loadout-can-option');
          if (mode === 'pick') {
            option.type = 'button';
            option.dataset.paLoadoutCanKey = key;
          } else {
            option.classList.add('is-included');
          }
          if (product.image) {
            const image = el('img');
            image.src = product.image;
            image.alt = '';
            image.loading = 'lazy';
            image.width = 96;
            image.height = 96;
            option.append(image);
          }
          option.append(el('span', '', product.title));
          return option;
        }));
      }
      if (mode === 'pick') {
        options.querySelectorAll('[data-pa-loadout-can-key]').forEach((option) => {
          const product = products.get(option.dataset.paLoadoutCanKey);
          option.setAttribute('aria-pressed', String(option.dataset.paLoadoutCanKey === loadout.picked));
          option.disabled = !product || product.max < loadout.quantities.get(loadout.picked);
        });
      }
    };
    const renderLoadouts = () => {
      const toCheckout = loadoutGoesToCheckout();
      const loadouts = loadoutCards.map(loadoutFor);
      loadouts.forEach((loadout, index) => {
        const { card, gifts, subtotal, available } = loadout;
        card.classList.toggle('is-unavailable', !available);
        card.querySelector('[data-pa-loadout-gifts]').textContent = String(gifts);
        card.querySelector('[data-pa-loadout-box-word]').textContent = boxWord(gifts);
        fillCrate(card.querySelector('[data-pa-loadout-crate]'), gifts);
        const worth = rewardWorth(gifts);
        card.querySelector('[data-pa-loadout-items]').replaceChildren(...loadoutLines(loadout).map((row) => {
          const item = el('li', row.free ? 'is-free' : '');
          const amount = el('span', 'pa-builder__loadout-qty');
          amount.append(el('b', '', row.count.toLocaleString('en-US')), document.createTextNode(' '), el('small', '', row.unit));
          const name = el('span', 'pa-builder__loadout-name');
          if (row.free) name.append(el('span', 'pa-builder__loadout-free-tag', 'FREE'), document.createTextNode(' '));
          name.append(document.createTextNode(row.name));
          item.append(amount, document.createTextNode(' '), name);
          return item;
        }));
        renderCanRow(card, loadout);
        card.querySelector('[data-pa-loadout-price]').textContent = available ? compactMoney(subtotal) : 'Sold out';
        const priceNote = card.querySelector('[data-pa-loadout-price-note]');
        priceNote.replaceChildren();
        if (available && gifts) priceNote.append(el('b', '', worth ? `${worth} FREE ${reward.label}` : `${gifts} FREE ${boxWord(gifts).toLowerCase()}`), document.createTextNode(' included'));
        const add = card.querySelector('[data-pa-loadout-add]');
        add.disabled = !available;
        card.querySelector('[data-pa-loadout-add-label]').textContent = !available
          ? 'Sold out right now'
          : toCheckout
            ? `Checkout with ${gifts} FREE ${boxWord(gifts).toLowerCase()}`
            : `Add loadout + ${gifts} FREE ${boxWord(gifts).toLowerCase()}`;
        add.setAttribute('aria-label', available
          ? `${toCheckout ? 'Check out with' : 'Add'} the ${loadout.title}${/loadout$/i.test(loadout.title) ? '' : ' loadout'}, ${compactMoney(subtotal)}, with ${gifts} free ${reward.label} ${gifts === 1 ? 'box' : 'boxes'}`
          : `${loadout.title} loadout is sold out`);
        // Tier comparison: what the next loadout up adds, or which bonus steps the top loadout includes.
        const upsell = card.querySelector('[data-pa-loadout-upsell]');
        const next = loadouts[index + 1];
        const reachedTiers = bonusTiers.filter((tier) => subtotal >= tier.at);
        upsell.replaceChildren();
        if (available && next && next.available && next.gifts > gifts) {
          const extra = next.gifts - gifts;
          upsell.append(
            document.createTextNode(`Step up to ${next.title} (+${compactMoney(next.subtotal - subtotal)}) for `),
            el('strong', '', `${extra} more FREE ${boxWord(extra).toLowerCase()}`)
          );
        } else if (available && !next && reachedTiers.length) {
          const bonus = reachedTiers.reduce((sum, tier) => sum + tier.bonus, 0);
          upsell.append(
            document.createTextNode('Top tier: '),
            el('strong', '', `includes all ${bonus} bonus ${boxWord(bonus).toLowerCase()}`),
            document.createTextNode(` from the ${reachedTiers.map((tier) => compactMoney(tier.at)).join(' + ')} steps`)
          );
        }
        upsell.hidden = !upsell.childNodes.length;
      });
    };
    function loadoutGoesToCheckout() {
      return root.dataset.loadoutDestination !== 'cart'
        && root.dataset.checkoutEnabled === 'true'
        && Boolean(window.PaBuilderCartSync);
    }
    // Swaps the whole basket for the loadout. Returns null when stock no longer covers it.
    const applyLoadout = (loadout) => {
      if (!selectionLedger || !loadout.available) return null;
      const previous = deriveBasketTotals();
      const normalized = selectionLedger.normalize([...loadout.quantities].map(([key, quantity]) => ({ id: nextSelectionId(), key, quantity })), limits);
      const accepted = selectionLedger.aggregate(normalized);
      if ([...loadout.quantities].some(([key, quantity]) => accepted[key] !== quantity)) return null;
      clearRewardPulse();
      clearRewardCelebration();
      selections = normalized;
      rebuildBasket();
      selectionVersion += 1;
      if (checkoutStatus) checkoutStatus.textContent = '';
      persistSelections();
      lastMutation = null;
      lastReward = null;
      persistFeedback();
      return { previous, next: deriveBasketTotals() };
    };
    const overlayPart = (selector) => loadoutOverlay.querySelector(selector);
    // Checkout progress in the unlock overlay, driven by the cart sync's real stages.
    const loadoutStepOrder = ['packing', 'rewards', 'checkout'];
    const setLoadoutStep = (stage) => {
      const steps = overlayPart('[data-pa-lo-steps]');
      if (!steps) return;
      const index = loadoutStepOrder.indexOf(stage);
      steps.querySelectorAll('[data-pa-lo-step]').forEach((item) => {
        const position = loadoutStepOrder.indexOf(item.dataset.paLoStep);
        item.classList.toggle('is-done', position < index);
        item.classList.toggle('is-active', position === index);
      });
      const bar = overlayPart('[data-pa-lo-bar]');
      if (bar) bar.style.width = ['34%', '70%', '94%'][index] || '0%';
      const status = overlayPart('[data-pa-lo-status]');
      const active = steps.querySelector(`[data-pa-lo-step="${stage}"] span`);
      if (status && active) status.textContent = `${active.textContent}…`;
    };
    const showLoadoutSteps = (visible) => {
      const steps = overlayPart('[data-pa-lo-steps]');
      const progressBar = overlayPart('[data-pa-lo-progress]');
      if (steps) steps.hidden = !visible;
      if (progressBar) progressBar.hidden = !visible;
      // The checklist carries the visible status; the status line stays for screen readers and errors.
      overlayPart('[data-pa-lo-status]').classList.toggle('visually-hidden', visible);
      const bar = overlayPart('[data-pa-lo-bar]');
      if (bar && !visible) bar.style.width = '0%';
    };
    const overlayWait = (ms) => new Promise((resolve) => { overlayTimers.push(window.setTimeout(resolve, ms)); });
    const closeLoadoutOverlay = () => {
      overlayTimers.forEach(clearTimeout);
      overlayTimers = [];
      if (!loadoutOverlay) return;
      loadoutOverlay.hidden = true;
      root.classList.remove('pa-builder--loadout-open');
      clearRewardCelebration();
    };
    // Full-screen unlock: the crate fills one box at a time, then gold confetti. Resolves when the
    // last box lands so checkout never redirects mid-animation.
    const playLoadoutUnlock = (loadout, totals) => new Promise((resolve) => {
      const { gifts } = totals;
      const count = overlayPart('[data-pa-lo-count]');
      const word = overlayPart('[data-pa-lo-word]');
      const crate = overlayPart('[data-pa-lo-crate]');
      const worth = overlayPart('[data-pa-lo-worth]');
      overlayPart('[data-pa-lo-kicker]').textContent = `${loadout.title}${/loadout$/i.test(loadout.title) ? '' : ' loadout'} locked in`;
      overlayPart('[data-pa-lo-review]').hidden = true;
      overlayPart('[data-pa-lo-status]').textContent = '';
      showLoadoutSteps(false);
      crate.replaceChildren();
      worth.textContent = '';
      count.textContent = '0';
      word.textContent = boxWord(gifts);
      loadoutOverlay.hidden = false;
      root.classList.add('pa-builder--loadout-open');
      overlayPart('[data-pa-lo-count]').closest('[tabindex]').focus({ preventScroll: true });
      const finish = () => {
        count.textContent = String(gifts);
        const value = rewardWorth(gifts);
        worth.textContent = `${(gifts * reward.rounds).toLocaleString('en-US')} FREE ROUNDS${value ? ` · ${value} OF FREE ${rewardName()}` : ''}`;
        const rect = crate.getBoundingClientRect();
        const width = window.innerWidth;
        const mobile = width < 750;
        appendConfettiBurst(
          rect.left + rect.width / 2,
          rect.top + rect.height / 2,
          Math.round((mobile ? 72 : 96) * 1.6),
          Math.min(width * (mobile ? .8 : .46), mobile ? 340 : 640) * 1.2,
          'loadout',
          gifts,
          bonusTiers.some((tier) => totals.subtotal >= tier.at)
        );
        resolve(performance.now());
      };
      if (reducedMotion.matches) {
        fillCrate(crate, gifts);
        finish();
        return;
      }
      const lead = 380;
      const step = Math.round(Math.max(70, Math.min(170, 1500 / Math.max(gifts, 1))));
      for (let slot = 1; slot <= gifts; slot += 1) {
        overlayTimers.push(window.setTimeout(() => {
          const box = el('i', 'pa-builder__crate-box is-new');
          if (bonusSlots.has(slot) || isMilestoneBox(slot)) box.classList.add('is-milestone');
          crate.append(box);
          count.textContent = String(slot);
        }, lead + step * (slot - 1)));
      }
      // One tick per landing box, then a long buzz with the confetti (Android only).
      const ticks = [];
      for (let slot = 1; slot <= Math.min(gifts, 20); slot += 1) ticks.push(16, step - 16);
      buzz([0, lead, ...ticks, 120, 70, 160]);
      overlayTimers.push(window.setTimeout(finish, lead + step * gifts + 200));
    });
    async function buyLoadout(card) {
      if (loadoutBusy || syncingCheckout) return;
      const loadout = loadoutFor(card);
      const applied = applyLoadout(loadout);
      if (!applied) {
        renderLoadouts();
        announceStatus(`${loadout.title} loadout is not available in that amount right now.`);
        return;
      }
      crateDrop = 0;
      render(applied.next);
      if (!loadoutOverlay) {
        setFlow('build', { focus: false });
        return showStage(stages.length - 1, true, true);
      }
      const status = overlayPart('[data-pa-lo-status]');
      loadoutBusy = true;
      const unlock = playLoadoutUnlock(loadout, applied.next);
      if (!loadoutGoesToCheckout()) {
        status.textContent = 'Opening your loadout review…';
        await unlock;
        await overlayWait(1100);
        closeLoadoutOverlay();
        loadoutBusy = false;
        setFlow('build', { focus: false });
        showStage(stages.length - 1, true, true);
        return;
      }
      const rewardsStep = overlayPart('[data-pa-lo-step="rewards"] span');
      if (rewardsStep) rewardsStep.textContent = `Locking in ${applied.next.gifts} free ${reward.label} ${boxWord(applied.next.gifts).toLowerCase()}`;
      showLoadoutSteps(true);
      setLoadoutStep('packing');
      syncingCheckout = true;
      try {
        // The cart sync runs while the crate fills, so most of the wait is hidden behind the reward.
        const [result, confettiAt] = await Promise.all([
          syncCartForCheckout({ onProgress: (stage) => setLoadoutStep(stage === 'verified' ? 'checkout' : stage) }),
          unlock
        ]);
        const confirmed = result.giftQuantity || applied.next.gifts;
        setLoadoutStep('checkout');
        status.textContent = `${confirmed} free ${boxWord(confirmed).toLowerCase()} confirmed. Opening secure checkout…`;
        // Let the confetti play before leaving, however early the cart finished.
        const celebrationHold = reducedMotion.matches ? 600 : 1500;
        await overlayWait(Math.max(250, celebrationHold - (performance.now() - (confettiAt || performance.now()))));
        goToCheckout();
      } catch (error) {
        await unlock;
        showLoadoutSteps(false);
        status.textContent = error && error.message || 'We could not prepare checkout. Your loadout is saved—please try again.';
        const review = overlayPart('[data-pa-lo-review]');
        review.hidden = false;
        review.focus({ preventScroll: true });
        loadoutBusy = false;
      } finally {
        syncingCheckout = false;
      }
    }
    loadoutOverlay?.addEventListener('keydown', (event) => {
      const review = overlayPart('[data-pa-lo-review]');
      if (event.key === 'Escape' && !review.hidden) {
        closeLoadoutOverlay();
        setFlow('build', { focus: false });
        showStage(stages.length - 1, true, true);
      }
    });
    renderLoadouts();

    // Page flow: free-ammo question → loadouts we like → builder, all on this URL. Each screen is a
    // history entry, so the browser Back button walks back through them like separate pages.
    const flowScreens = [...root.querySelectorAll('[data-pa-flow-screen]')];
    const hasFlowScreen = (step) => flowScreens.some((screen) => screen.dataset.paFlowScreen === step);
    const applyRewardCopy = () => {
      root.querySelectorAll('[data-pa-reward-name]').forEach((node) => {
        node.textContent = node.dataset.paRewardName === 'lower' ? reward.label : rewardName();
      });
      root.querySelectorAll('[data-pa-reward-choice], [data-pa-reward-switch]').forEach((button) => {
        const key = button.dataset.paRewardChoice || button.dataset.paRewardSwitch;
        button.setAttribute('aria-pressed', String(key === reward.key && (rewardChosen || Boolean(button.dataset.paRewardSwitch))));
      });
    };
    const setReward = (key) => {
      const next = findReward(key);
      if (!next) return;
      reward = next;
      rewardChosen = true;
      rewardValue = Number(reward.value) || 0;
      try { sessionStorage.setItem('pa-builder-reward-v1', reward.key); } catch (_) {}
      if (checkoutStatus) checkoutStatus.textContent = '';
      applyRewardCopy();
      renderLoadouts();
      showStage(stageIndex, false);
      render();
    };
    const setFlow = (requested, { push = true, scroll = true, focus = true } = {}) => {
      const step = hasFlowScreen(requested) ? requested : 'build';
      root.dataset.paFlow = step;
      flowScreens.forEach((screen) => { screen.hidden = screen.dataset.paFlowScreen !== step; });
      try { sessionStorage.setItem('pa-builder-flow-v1', step); } catch (_) {}
      if (push && (!history.state || history.state.paFlow !== step)) history.pushState({ ...(history.state || {}), paFlow: step }, '');
      if (step === 'build') showStage(stageIndex, false);
      if (scroll) window.scrollTo(0, 0);
      updateJourneyState();
      if (!focus) return;
      const screen = flowScreens.find((item) => item.dataset.paFlowScreen === step);
      (step === 'build' ? root.querySelector('[data-pa-stage-title]') : screen && screen.querySelector('h1, h2'))?.focus({ preventScroll: true });
    };
    // Ads and emails can skip ahead: ?reward=5.56&step=loadouts.
    const flowParams = new URLSearchParams(window.location.search);
    const linkedReward = findReward(flowParams.get('reward'));
    if (linkedReward) {
      reward = linkedReward;
      rewardChosen = true;
      rewardValue = Number(reward.value) || 0;
      try { sessionStorage.setItem('pa-builder-reward-v1', reward.key); } catch (_) {}
    }
    let initialFlow = history.state && history.state.paFlow;
    if (!initialFlow && hasFlowScreen(flowParams.get('step'))) initialFlow = flowParams.get('step');
    if (!initialFlow) { try { initialFlow = sessionStorage.getItem('pa-builder-flow-v1'); } catch (_) {} }
    if (!hasFlowScreen(initialFlow)) initialFlow = flowScreens.length ? flowScreens[0].dataset.paFlowScreen : 'build';
    history.replaceState({ ...(history.state || {}), paFlow: initialFlow }, '');
    window.addEventListener('popstate', (event) => {
      const step = event.state && event.state.paFlow;
      if (!step || step === root.dataset.paFlow) return;
      closeLoadoutOverlay();
      setFlow(step, { push: false });
    });

    root.addEventListener('click', (event) => {
      if (event.target.closest('[data-pa-checkout]')) return void checkout();
      const rewardChoice = event.target.closest('[data-pa-reward-choice]');
      if (rewardChoice && root.contains(rewardChoice)) {
        setReward(rewardChoice.dataset.paRewardChoice);
        return setFlow(hasFlowScreen('loadouts') ? 'loadouts' : 'build');
      }
      const rewardSwitch = event.target.closest('[data-pa-reward-switch]');
      if (rewardSwitch && root.contains(rewardSwitch)) return setReward(rewardSwitch.dataset.paRewardSwitch);
      const flowGo = event.target.closest('[data-pa-flow-go]');
      if (flowGo && root.contains(flowGo)) return setFlow(flowGo.dataset.paFlowGo);
      const canOption = event.target.closest('[data-pa-loadout-can-key]');
      if (canOption && root.contains(canOption)) {
        pickedCans.set(canOption.closest('[data-pa-loadout]'), canOption.dataset.paLoadoutCanKey);
        renderLoadouts();
        return;
      }
      const loadoutAdd = event.target.closest('[data-pa-loadout-add]');
      if (loadoutAdd && root.contains(loadoutAdd)) return void buyLoadout(loadoutAdd.closest('[data-pa-loadout]'));
      if (event.target.closest('[data-pa-lo-review]')) {
        closeLoadoutOverlay();
        setFlow('build', { focus: false });
        return showStage(stages.length - 1, true, true);
      }
      const gapCloser = event.target.closest('[data-pa-gap-closer]');
      if (gapCloser && root.contains(gapCloser)) {
        addSelection(gapCloser.dataset.paGapKey, Number(gapCloser.dataset.paGapQuantity));
        return;
      }
      const repeatAddButton = event.target.closest('[data-pa-add-repeat]');
      if (repeatAddButton && root.contains(repeatAddButton)) {
        const key = repeatAddButton.dataset.paRepeatKey;
        const increment = Number(repeatAddButton.dataset.paRepeatQuantity);
        if (!key || !increment) return;
        if (repeatAddButton.getAttribute('aria-disabled') === 'true') {
          announceStatus(repeatAddButton.dataset.paUnavailableMessage);
          return;
        }
        const restoreFocus = document.activeElement === repeatAddButton;
        const transition = addSelection(key, increment);
        if (transition && restoreFocus) {
          requestAnimationFrame(() => {
            const inCartStage = stages[stageIndex].key === 'cart';
            const scope = inCartStage
              ? [...lines.querySelectorAll('[data-pa-line]')].find((line) => line.dataset.paLine === key)
              : cards.find((card) => card.dataset.key === key);
            const sameRepeat = [...(scope?.querySelectorAll('[data-pa-add-repeat]') || [])].find((button) => {
              return button.dataset.paRepeatKey === key
                && Number(button.dataset.paRepeatQuantity) === increment
                && button.getAttribute('aria-disabled') !== 'true';
            });
            const matchingDecrement = [...(scope?.querySelectorAll('[data-pa-remove-selection]') || [])].find((button) => {
              return button.dataset.paRepeatKey === key
                && Number(button.dataset.paRepeatQuantity) === increment;
            });
            (sameRepeat || matchingDecrement || scope?.querySelector('[data-pa-remove-selection]'))?.focus({ preventScroll: true });
          });
        }
        return;
      }
      const removeSelectionButton = event.target.closest('[data-pa-remove-selection]');
      if (removeSelectionButton && root.contains(removeSelectionButton)) {
        const id = removeSelectionButton.dataset.paRemoveSelection;
        const selection = selections.find((item) => item.id === id);
        const key = selection && selection.key;
        const repeatKey = removeSelectionButton.dataset.paRepeatKey;
        const repeatQuantity = Number(removeSelectionButton.dataset.paRepeatQuantity);
        const restoreFocus = document.activeElement === removeSelectionButton;
        const transition = removeSelection(id);
        if (transition && restoreFocus) {
          requestAnimationFrame(() => {
            const productCard = cards.find((card) => card.dataset.key === key);
            const cartGroup = [...lines.querySelectorAll('[data-pa-line]')].find((line) => line.dataset.paLine === key);
            const inCartStage = stages[stageIndex].key === 'cart';
            const scope = inCartStage ? cartGroup : productCard;
            const sameRepeat = repeatKey && repeatQuantity
              ? [...(scope?.querySelectorAll('[data-pa-remove-selection]') || [])].find((button) => {
                return button.dataset.paRepeatKey === repeatKey
                  && Number(button.dataset.paRepeatQuantity) === repeatQuantity;
              })
              : null;
            const expandedMatch = repeatQuantity
              ? [...(scope?.querySelectorAll('[data-pa-remove-selection]') || [])].find((button) => {
                return Number(button.dataset.paSelectionQuantity) === repeatQuantity;
              })
              : null;
            const repeatCandidates = [...(scope?.querySelectorAll('[data-pa-remove-selection][data-pa-repeat-quantity]') || [])];
            const nearestRepeat = repeatQuantity && repeatCandidates.length
              ? repeatCandidates.find((button) => Number(button.dataset.paRepeatQuantity) >= repeatQuantity)
                || repeatCandidates[repeatCandidates.length - 1]
              : null;
            const nextRemove = sameRepeat || expandedMatch || nearestRepeat || (inCartStage
              ? cartGroup?.querySelector('[data-pa-remove-selection]') || lines.querySelector('[data-pa-remove-selection]') || lines.querySelector('[data-pa-shop-popular]')
              : productCard?.querySelector('[data-pa-remove-selection]') || productCard?.querySelector('[data-pa-add]:not(:disabled)'))
              || root.querySelector('[data-pa-stage-title]');
            nextRemove?.focus({ preventScroll: true });
          });
        }
        return;
      }
      const add = event.target.closest('[data-pa-add]');
      if (add && root.contains(add)) {
        const key = add.closest('[data-pa-product]').dataset.key;
        const increment = Number(add.dataset.paAdd);
        if (!increment || add.disabled) return;
        const keyboardActivation = event.detail === 0 && document.activeElement === add;
        const transition = addSelection(key, increment);
        if (!transition) return;
        if (keyboardActivation) {
          const card = add.closest('[data-pa-product]');
          const receipt = card.querySelector('[data-pa-product-receipt]');
          const fallback = card.querySelector('[data-pa-add]:not(:disabled)') || card.querySelector('[data-pa-remove-selection]');
          requestAnimationFrame(() => (receipt && !receipt.hidden ? receipt : fallback)?.focus({ preventScroll: true }));
        }
        return;
      }
      if (event.target.closest('[data-pa-receipt-cart]')) return showStage(stages.length - 1, true, true);
      const step = event.target.closest('[data-pa-step]');
      if (step && root.contains(step)) return showStage(stages.findIndex((item) => item.key === step.dataset.paStep), true, event.detail === 0);
      if (event.target.closest('[data-pa-continue]')) return showStage(stageIndex + 1, true, true);
      if (event.target.closest('[data-pa-back]')) return showStage(stageIndex - 1, true, true);
      if (event.target.closest('[data-pa-shop-popular]')) return showStage(0, true, true);
      if (event.target.closest('[data-pa-keep-building]')) return showStage(lastShopStage, true, true);
      if (event.target.closest('[data-pa-mobile-next]')) return showStage(stageIndex + 1, true, true);
      if (event.target.closest('[data-pa-view-basket]')) return showStage(stages.length - 1, true, true);
    });
    const updateJourneyState = () => {
      const shop = root.querySelector('.pa-builder__shop');
      if (!shop) return;
      const shopTop = shop.getBoundingClientRect().top + window.scrollY;
      root.classList.toggle('pa-builder--journey-active', window.scrollY >= shopTop - 1);
    };
    compactCrate.addEventListener('change', () => render());
    window.addEventListener('scroll', updateJourneyState, { passive: true });
    window.addEventListener('resize', updateJourneyState);
    window.addEventListener('pagehide', () => {
      clearRewardPulse();
      clearRewardCelebration();
      cancelAnimationFrame(statusAnnouncementFrame);
      statusAnnouncementFrame = null;
      pendingAnnouncement = '';
    });
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        clearRewardPulse();
        closeLoadoutOverlay();
        loadoutBusy = false;
        syncingCheckout = false;
        render();
      }
      updateJourneyState();
    });
    applyRewardCopy();
    renderLoadouts();
    showStage(0, false);
    render();
    setFlow(initialFlow, { push: false, scroll: false, focus: false });
  });
})();
